import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

/** Shared layout for /for/* stakeholder purchase-decision pages (AD page is the template). */
export function StakeholderPage({ testid, eyebrow, EyebrowIcon, headline, headlineSub, intro, risks, risksHeading, risksSub, surface, surfaceHeading, surfaceSub, ctaHeading, ctaSub, demoLabel }) {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid={testid}>
      <SiteNav />

      <section className="pt-32 pb-14 border-b border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3 flex items-center gap-2">
          <EyebrowIcon className="h-3.5 w-3.5" /> {eyebrow}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]" data-testid={`${testid}-headline`}>
          {headline}<br /><span className="text-white/50">{headlineSub}</span>
        </h1>
        <p className="mt-6 text-white/70 max-w-3xl leading-relaxed">{intro}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/request-demo" data-testid={`${testid}-cta-demo`}>
            <Button className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
              {demoLabel} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/enterprise/contact" data-testid={`${testid}-cta-enterprise`}>
            <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
              Talk to enterprise sales
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 max-w-6xl mx-auto px-6">
        <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight leading-[1.1]">
          {risksHeading}<br /><span className="text-white/50">{risksSub}</span>
        </h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid={`${testid}-risks`}>
          {risks.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.label} className="ink-card rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${r.tone}22`, color: r.tone }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="font-display font-black text-white text-lg">{r.label}</div>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-red-300/80 font-bold mb-1">The problem</div>
                    <div className="text-white/80 leading-relaxed">{r.body}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80 font-bold mb-1">What BioHacker changes</div>
                    <div className="text-white/80 leading-relaxed">{r.relief}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 border-t border-white/8 max-w-6xl mx-auto px-6">
        <h2 className="font-display font-black text-3xl text-white tracking-tight leading-[1.1]">
          {surfaceHeading}<br /><span className="text-white/50">{surfaceSub}</span>
        </h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid={`${testid}-surface`}>
          {surface.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="ink-card rounded-2xl p-6">
                <Icon className="h-6 w-6 text-fuchsia-300 mb-3" />
                <div className="font-display font-black text-white text-lg">{c.label}</div>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 border-t border-white/8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight">{ctaHeading}</h2>
          <p className="mt-3 text-white/60 max-w-2xl mx-auto">{ctaSub}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/request-demo">
              <Button className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
                {demoLabel} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/enterprise/contact">
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
                Talk to enterprise sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
